import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { api, formatApiErrorDetail, setTerminalReadOnly } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ShieldAlert, MonitorCheck, Monitor, MonitorX } from "lucide-react";
import { toast } from "sonner";
import { registerStations } from "../lib/ticketPrint";
import { getDeviceId } from "../lib/device";

const SecurityContext = createContext(null);

const ACTION_LABEL = {
  cancel: "Cancelación / Devolución",
  price: "Cambio de precio en POS",
  discount: "Descuento manual",
  cash_out: "Retiro / gasto de caja",
  delete: "Eliminar registro",
  remove_item: "Quitar producto del cobro",
};

const STATION_KEY = "pos_station_id";

export function SecurityProvider({ children }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [actions, setActions] = useState({});
  const [stations, setStations] = useState([]);
  const [hiddenModules, setHiddenModules] = useState([]);
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [stationPasswordSet, setStationPasswordSet] = useState(false);
  const [whatsappSales, setWhatsappSales] = useState(false);
  const deviceId = getDeviceId();

  // Modal de autorización (step-up admin)
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("cancel");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const resolver = useRef(null);

  // Modal de configuración de terminal
  const [termOpen, setTermOpen] = useState(false);
  const [autoLockMinutes, setAutoLockMinutes] = useState(0);

  const isBusiness = user && user.role !== "superadmin";
  const isManager = user && ["admin", "manager"].includes(user.role);

  const loadCfg = useCallback(async () => {
    if (!isBusiness) return;
    try {
      const { data } = await api.get("/config");
      setActions(data?.security_actions || {});
      setStations(data?.stations || []);
      registerStations(data?.stations || []);
      setHiddenModules(data?.hidden_modules || []);
      setWhatsappSales(data?.whatsapp_sales === true);
      setStationPasswordSet(!!data?.station_password_set);
      setCfgLoaded(true);
      setAutoLockMinutes(Number(data?.auto_lock_minutes) || 0);
    } catch { /* noop */ }
  }, [isBusiness]);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  // Detección por EQUIPO: este equipo es terminal solo si su device_id está vinculado a una estación.
  const myStation = stations.find((s) => s.device_id && s.device_id === deviceId) || null;
  const stationId = myStation?.id || "";
  const terminalConfigured = !!myStation;
  const readOnly = !!isBusiness && !terminalConfigured;

  useEffect(() => {
    if (stationId) localStorage.setItem(STATION_KEY, stationId);
    else localStorage.removeItem(STATION_KEY);
  }, [stationId]);
  useEffect(() => { setTerminalReadOnly(readOnly); }, [readOnly]);
  useEffect(() => () => setTerminalReadOnly(false), []);

  const configureTerminal = useCallback(async (id, extra = {}) => {
    try {
      if (extra.takeover && id) {
        await api.post("/stations/unbind", { station_id: id, device_id: "" });
      }
      if (id || extra.newName) {
        await api.post("/stations/bind", { station_id: id || "", device_id: deviceId, password: extra.password || "", new_name: extra.newName || "" });
        toast.success("Este equipo quedó activado como terminal");
      } else {
        await api.post("/stations/unbind", { station_id: "", device_id: deviceId });
        toast.success("Equipo desvinculado · modo solo lectura");
      }
      setTermOpen(false);
      await loadCfg();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo configurar la terminal");
      return false;
    }
  }, [deviceId, loadCfg]);

  const openTerminalConfig = useCallback(() => setTermOpen(true), []);

  // Autorización: Promise<{ ok, password }>. Resuelve al instante si no requiere autorización.
  const ensureAuthorized = useCallback((act) => {
    if (isManager || !actions?.[act]) return Promise.resolve({ ok: true, password: "" });
    setAction(act); setPwd(""); setOpen(true);
    return new Promise((resolve) => { resolver.current = resolve; });
  }, [isManager, actions]);

  const finish = (result) => {
    setOpen(false);
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-admin", { password: pwd });
      toast.success(`Autorizado por ${data.authorized_by}`);
      finish({ ok: true, password: pwd });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Contraseña inválida");
    } finally { setLoading(false); }
  };

  return (
    <SecurityContext.Provider value={{ actions, ensureAuthorized, refreshSecurity: loadCfg, stations, stationId, terminalConfigured, readOnly, configureTerminal, openTerminalConfig, hiddenModules, whatsappSales, autoLockMinutes, cfgLoaded, stationPasswordSet, deviceId }}>
      {children}

      {/* Autorización de administrador / encargado */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) finish({ ok: false, password: "" }); }}>
        <DialogContent className="max-w-sm" data-testid="admin-override-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ShieldAlert size={18} className="text-amber-500" /> Autorización requerida</DialogTitle>
            <DialogDescription>Esta acción ({ACTION_LABEL[action] || action}) necesita la contraseña de un administrador o encargado.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Contraseña de administrador / encargado</Label>
              <Input type="password" required autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="admin-override-password" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => finish({ ok: false, password: "" })} data-testid="admin-override-cancel">Cancelar</Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold" data-testid="admin-override-confirm">{loading ? "Verificando…" : "Autorizar"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Configuración de terminal de este equipo */}
      <Dialog open={termOpen} onOpenChange={setTermOpen}>
        <DialogContent className="max-w-sm" data-testid="terminal-config-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {terminalConfigured ? <MonitorCheck size={18} className="text-emerald-600" /> : <MonitorX size={18} className="text-red-500" />}
              Terminal de este equipo
            </DialogTitle>
            <DialogDescription>El sistema detecta automáticamente si ESTE equipo está registrado como terminal. Sin registro, queda en modo solo lectura.</DialogDescription>
          </DialogHeader>

          {terminalConfigured ? (
            <div className="space-y-3" data-testid="terminal-bound-state">
              <div className="card-surface px-3 py-3 flex items-center gap-2 border-emerald-500">
                <MonitorCheck size={16} className="text-emerald-500" />
                <span className="flex-1 text-sm">Este equipo es la terminal <b>{myStation?.name}</b> <span className="text-xs text-muted-foreground">· {myStation?.printer}{myStation?.scale ? " · báscula" : ""}</span></span>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-start gap-2" data-testid="terminal-locked-note">
                <ShieldAlert size={14} className="shrink-0 mt-0.5" />
                <span>Esta terminal quedó fija en este equipo y no se puede cambiar ni desvincular desde aquí, para no desconfigurar la terminal de otro equipo. Si necesitas reasignarla, un administrador debe liberarla en <b>Configuración → Estaciones</b>.</span>
              </div>
            </div>
          ) : stations.length === 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Aún no hay terminales/estaciones creadas.</p>
              {isManager ? (
                <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" onClick={() => { setTermOpen(false); nav("/settings"); }} data-testid="terminal-goto-settings">Crear terminal en Configuración</Button>
              ) : (
                <p className="text-xs text-amber-600">Pide a un administrador que configure las terminales en Configuración → Estaciones.</p>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="terminal-station-list">
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-600 font-medium" data-testid="terminal-not-registered">
                Este equipo NO está configurado como terminal.
              </div>
              {isManager ? (
                <>
                  <p className="text-xs text-muted-foreground">Registra este equipo como una de las terminales disponibles:</p>
                  {stations.map((s) => {
                    const takenByOther = s.device_id && s.device_id !== deviceId;
                    return (
                      <button key={s.id} disabled={takenByOther} onClick={() => configureTerminal(s.id)} data-testid={`terminal-pick-${s.id}`}
                        className={`w-full text-left card-surface px-3 py-2.5 flex items-center gap-2 ${takenByOther ? "opacity-50 cursor-not-allowed" : "hover:border-emerald-500/60"}`}>
                        <Monitor size={15} className={takenByOther ? "text-slate-400" : "text-emerald-500"} />
                        <span className="flex-1"><b>{s.name}</b> <span className="text-xs text-muted-foreground">· {s.printer}{s.scale ? " · báscula" : ""}</span></span>
                        {takenByOther && <span className="text-[11px] font-semibold text-amber-600">vinculada a otro equipo</span>}
                      </button>
                    );
                  })}
                </>
              ) : (
                <p className="text-xs text-amber-600">Este equipo no está en la lista de terminales. Pide a un administrador que lo configure como terminal para poder vender.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SecurityContext.Provider>
  );
}

export const useSecurity = () => useContext(SecurityContext) || { actions: {}, ensureAuthorized: () => Promise.resolve({ ok: true, password: "" }), refreshSecurity: () => {}, stations: [], stationId: "", terminalConfigured: true, readOnly: false, configureTerminal: () => {}, openTerminalConfig: () => {} };
